import React, { useMemo } from "react";
import { Box, Divider, Drawer, IconButton, Stack, Typography, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { fetchUsers } from "../../../fetchers/user";
import { useToolServiceHistory } from "../../../hooks/useToolServiceHistory";
import cmmsMessages, { getTriggerBasisLabel } from "../../cmms/cmmsMessages";
import ToolServiceHistoryPanel from "../../cmms/components/ToolServiceHistoryPanel";
import ToolServiceStatusCell from "../../cmms/components/ToolServiceStatusCell";
import ScheduleResetButton from "../../cmms/components/ScheduleResetButton";
import { buildToolServiceSummary } from "../../cmms/toolServiceStatus";
import { normalizeScheduleReading } from "../../cmms/scheduleReset";
import {
  cmmsTypeScale,
  getAssetHierarchyPalette,
  getPanelTitleSx,
  getSectionBoxSx,
  getSectionLabelSx,
} from "../../cmms/assetHierarchyStyles";
import messages from "../messages";

/*
 * One tool: every schedule watching it, and every service already booked against it.
 *
 * The register's grid can only show one reading per row — a bar has room for one number, and the
 * reassuring one is the wrong one to show, so it shows the worst. This is where the rest of the
 * answer lives: a tool watched in hours *and* in cycles has two readings, and the second one is
 * invisible anywhere else in the module.
 *
 * A sibling of features/tools/components/ToolServiceHistoryDrawer rather than a reuse of it. That
 * one is CMMS chrome that happens to live in an OEE feature folder; importing it here would point
 * a CMMS feature at features/tools, and it renders no schedules. What is genuinely shared — the
 * status cell and the history panel — is imported from features/cmms, where both already live
 * precisely so a second mount point costs a wrapper.
 *
 * Always mounted, opened on `tool`. That is what lets it animate out, while `enabled` on the two
 * queries keeps them from firing before a row is clicked.
 */
export default function ToolServiceDrawer({ tool, onClose }) {
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const intl = useIntl();

  const toolId = tool?.id ?? null;
  const open = Boolean(toolId);

  const history = useToolServiceHistory(toolId);

  // The readings come down from the page, which is already holding the company-wide list. Asking
  // /api/tool-monitoring/status?toolId= here would be a second request for rows we have.
  const statuses = tool?.statuses ?? [];

  // Same key and staleTime as the machine page's work order tab, so whichever renders first warms
  // the other. Gated on `open` because the register itself never needs the user list.
  const usersQuery = useQuery(["users"], () => fetchUsers(), {
    enabled: open,
    staleTime: 300000,
  });

  const userNameById = useMemo(() => {
    const map = new Map();
    (usersQuery.data ?? []).forEach((user) => {
      map.set(user.id, user.name ?? user.username ?? `#${user.id}`);
    });
    return map;
  }, [usersQuery.data]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 520 }, backgroundColor: cmmsColors.surface } }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ p: 2.5, borderBottom: `1px solid ${cmmsColors.border}` }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant={cmmsTypeScale.panelTitle} sx={getPanelTitleSx(cmmsColors)}>
                {tool?.name}
              </Typography>
              {tool?.description &&
                <Typography variant="caption" sx={{ color: cmmsColors.mutedText }}>
                  {tool.description}
                </Typography>
              }
            </Box>
            <IconButton onClick={onClose} aria-label={intl.formatMessage(cmmsMessages.close)}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, overflow: "auto", px: 2.5, py: 2 }}>
          <Typography variant={cmmsTypeScale.sectionLabel} sx={{ ...getSectionLabelSx(cmmsColors), display: "block" }}>
            {intl.formatMessage(messages.drawerSchedules)}
          </Typography>

          {/*
            * Every reading, not the worst one. The grid already showed the worst; what it could
            * not show is that there is a second schedule at all, which is exactly the thing
            * someone opens this drawer to find out.
            */}
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {statuses.length === 0 &&
              <Typography variant="body2" sx={{ color: cmmsColors.mutedText }}>
                {intl.formatMessage(cmmsMessages.toolStatusNotWatched)}
              </Typography>
            }
            {statuses.map((status) => (
              <Box key={status.scheduleId} sx={getSectionBoxSx(cmmsColors)}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {status.scheduleTitle}
                  </Typography>
                  <Typography variant="caption" sx={{ color: cmmsColors.mutedText, flexShrink: 0 }}>
                    {getTriggerBasisLabel(status.triggerBasis, intl)}
                  </Typography>
                </Stack>

                {/* The same cell the grid draws, so one reading looks the same in both places. */}
                <Box sx={{ mt: 0.5 }}>
                  <ToolServiceStatusCell status={status} />
                </Box>

                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.75 }}>
                  {buildToolServiceSummary(status, intl)}
                </Typography>

                {/*
                  * Reset here as well as on the schedules page. This is where someone notices a
                  * tool at 98 %, and sending them to another page to act on it is how a reading
                  * gets ignored.
                  *
                  * No reset history section beside it, and that is not an omission: a tool-lane
                  * reset writes a ToolServiceLog, so the entry lands in the service history this
                  * drawer already renders below. A second list would show the same rows twice.
                  *
                  * `lastResetAt` is deliberately not passed. The log is not read here, and the
                  * status's own lastServicedAt — which this lane's anchor genuinely is — carries
                  * the date instead, labelled as a service rather than as a reset.
                  */}
                <Box sx={{ mt: 1 }}>
                  <ScheduleResetButton
                    schedule={{
                      id: status.scheduleId,
                      title: status.scheduleTitle,
                      toolId: status.toolId,
                      triggerBasis: status.triggerBasis,
                    }}
                    reading={normalizeScheduleReading({ toolStatus: status })}
                  />
                </Box>
              </Box>
            ))}
          </Stack>

          <Divider sx={{ my: 2.5 }} />

          <Typography variant={cmmsTypeScale.sectionLabel} sx={{ ...getSectionLabelSx(cmmsColors), display: "block", mb: 1 }}>
            {intl.formatMessage(messages.drawerHistory)}
          </Typography>

          <ToolServiceHistoryPanel
            entries={history.entries}
            // The user list is folded into the same skeleton rather than raced against it: letting
            // the entries land first would show "#1016" for a moment before it turned into a name.
            // A failed user read is not waited on — isInitialLoading clears on error, and the ids
            // are then the answer.
            isLoading={history.isLoading || usersQuery.isInitialLoading}
            isError={history.isError}
            error={history.error}
            onRetry={() => history.refetch()}
            isRetrying={history.isFetching}
            userNameById={userNameById}
          />
        </Box>
      </Box>
    </Drawer>
  );
}
